import {
  collection,
  collectionGroup,
  doc,
  getDocs,
  type CollectionReference,
  type DocumentData,
  type DocumentReference,
} from 'firebase/firestore'
import { db } from '@/services/firebase'

/** Returns a collection group query handle for a collection name across all institutes/users. */
export function getAllCollectionEntries(collectionName: string) {
  return collectionGroup(db, collectionName)
}

/** Returns a typed collection reference under institutes/{instituteId}/{collectionName}. */
export function getInstituteCollection(collectionName: string, instituteId: string) {
  return collection(db, 'institutes', instituteId, collectionName)
}

/** Returns parent instituteId for documents under institutes/{instituteId}/{collection}/{id}. */
export function getInstituteIdFromPath(path: string): string | null {
  const segments = path.split('/')
  const idx = segments.indexOf('institutes')
  if (idx === -1) return null
  return segments[idx + 1] ?? null
}

/** Returns a typed collection reference under users/{userId}/institutes/{instituteId}/{collectionName}. */
export function getUserInstituteCollection(
  collectionName: string,
  userId: string,
  instituteId: string
) {
  return collection(db, 'users', userId, 'institutes', instituteId, collectionName)
}

/** Resolves a document reference in a collection group by exact document ID, or null if not found. */
export async function resolveCollectionDocRef(
  collectionName: string,
  id: string
): Promise<DocumentReference<DocumentData> | null> {
  const allSnap = await getDocs(getAllCollectionEntries(collectionName))
  const match = allSnap.docs.find((docSnap) => docSnap.id === id)
  return match?.ref ?? null
}

/** Resolves a document reference by ID and throws when no matching document exists. */
export async function requireCollectionDocRef(
  collectionName: string,
  id: string
): Promise<DocumentReference<DocumentData>> {
  const ref = await resolveCollectionDocRef(collectionName, id)
  if (!ref) {
    throw new Error(`[firestorePaths] ${collectionName}/${id} not found`)
  }
  return ref
}

/** Returns a typed collection reference under global/root/{collectionName}.
 *  The intermediate 'root' document is required so the path has an odd number
 *  of segments (global/root/<collection> = 3), which the Firestore SDK
 *  requires for collection references. */
export function getGlobalCollection(
  collectionName: string
): CollectionReference<DocumentData> {
  return collection(doc(db, 'global', 'root'), collectionName)
}

/** Returns a collection reference based on scope ('global' or institute ID). */
export function resolveScopedCollection(
  collectionName: string,
  scopeId: string | 'global'
): CollectionReference<DocumentData> {
  return scopeId === 'global'
    ? getGlobalCollection(collectionName)
    : getInstituteCollection(collectionName, scopeId)
}

/** Returns a document reference in the correct scope. */
export function resolveScopedDocRef(
  collectionName: string,
  docId: string,
  scopeId: string | 'global'
): DocumentReference<DocumentData> {
  const col = resolveScopedCollection(collectionName, scopeId)
  return doc(col, docId)
}